/**
 * Which Tab presses the recording start guide may take (TOADO-969).
 *
 * The guide keeps Tab among its own controls while it is open, but a dialog,
 * menu or popover opened over it keeps its own. This decides which is which,
 * from the key's composed path and what has focus. Kept apart from the guide's
 * component so its tests can build DOM the app never does (the guide's own
 * dialog inside a shadow root, slotted overlays) and test it directly.
 */

const OVERLAY_ROLES = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

/**
 * Every element from `node` out to the document, as an event's composed path
 * runs: into the slot a node is assigned to, and out of a shadow root to its
 * host.
 */
function outFrom(node: Element | null): Element[] {
  const chain: Element[] = [];
  for (let at: Element | null = node; at; ) {
    chain.push(at);
    const root = at.parentNode;
    at = at.assignedSlot ?? at.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
  }
  return chain;
}

/** The element in the document's own tree that stands for `node`: its outermost shadow host, or itself. */
function outermost(node: Element): Element {
  let at = node;
  while (at.getRootNode() instanceof ShadowRoot) at = (at.getRootNode() as ShadowRoot).host;
  return at;
}

/**
 * Whether a Tab belongs to something other than the guide, so the guide
 * leaves it alone: focus in anything opened over it (a dialog, a menu, a
 * popover, portalled after it, or one with one of those roles mounted earlier
 * and shown later, such as a palette kept in the page). Focus left on the page
 * behind it (before it in the document, with no such role) is the guide's to
 * bring back. An overlay with none of these roles, mounted before the guide,
 * is not recognised.
 *
 * `path` is the key's composed path, through shadow roots: a web component
 * may carry its role on its host and its control inside. A key sent to the
 * document or its body has no element of its own on it, and focus says,
 * walked out the same way a composed path would be.
 */
export function keyBelongsElsewhere(dialog: Element, path: readonly EventTarget[], active: Element | null): boolean {
  const doc = dialog.ownerDocument;
  // The document's own elements stand for no overlay, whichever way the chain is read.
  const ownElement = (node: EventTarget): node is Element => node instanceof Element && node !== doc.documentElement && node !== doc.body;
  const targets = path.filter(ownElement);
  const holder = targets[0] ?? active;
  if (!holder) return false;
  const chain = targets.length > 0 ? targets : outFrom(holder).filter(ownElement);
  // Order is compared in the document's own tree: each side stands for its
  // outermost shadow host, the guide's dialog as much as the focused node.
  const outer = outermost(holder);
  // In the guide, whether focus or the guide sits inside a shadow root.
  if (outer === doc.body || dialog.contains(holder) || dialog.contains(outer)) return false;
  // Nodes not in one document have no order to read: not counted as after.
  const position = outermost(dialog).compareDocumentPosition(outer);
  const after = !(position & Node.DOCUMENT_POSITION_DISCONNECTED) && !!(position & Node.DOCUMENT_POSITION_FOLLOWING);
  // The chain runs out from focus, which is not in the guide (that returned
  // above), so the guide's own dialog is never on it.
  const inOverlay = chain.some((node) => node.matches(OVERLAY_ROLES));
  return after || inOverlay;
}
